// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
const IMMUNE_FIDELITY_PURE_TS = [
    {
        code: "P",
        name: "PERMIT",
        mode: "immune",
        fidelity: "compile-time",
        trustZone: "Zone1-compile",
        mechanism: "Capability<R,Op> phantom type — violation is a type error",
    },
    {
        code: "S",
        name: "SEPARATE",
        mode: "immune",
        fidelity: "compile-time",
        trustZone: "Zone1-compile",
        mechanism: "DataValue<T>/Instruction<T> brands — domain crossing is a type error",
    },
    {
        code: "D1",
        name: "DATA.ORIGIN",
        mode: "immune",
        fidelity: "compile-time",
        trustZone: "Zone1-compile",
        mechanism: "Provenanced<T,O> phantom origin — provenance detachment is a type error",
    },
    {
        code: "D2",
        name: "DATA.REACH",
        mode: "immune",
        fidelity: "compile-time",
        trustZone: "Zone1-compile",
        mechanism: "Classified<T,C> phantom level — upward flow is a type error",
    },
    {
        code: "W",
        name: "PRECOMMIT",
        mode: "immune",
        fidelity: "compile-time",
        trustZone: "Zone1-compile",
        mechanism: "Effect<Uncommitted/Committed/Cancelled> typestate — phase violation is a type error",
    },
];
const CONTAINED_INERT_FIDELITY_WASM = [
    {
        code: "R1",
        name: "RES.CONSUME",
        mode: "contained",
        fidelity: "wasm-structural",
        trustZone: "Zone2-wasm",
        mechanism: "Wasm ResourceRegistry in linear memory — consumed state unreachable from JS",
    },
    {
        code: "R2",
        name: "RES.RELEASE",
        mode: "contained",
        fidelity: "wasm-structural",
        trustZone: "Zone2-wasm",
        mechanism: "Rust Drop trait in Wasm linear memory — cleanup guaranteed by Wasm GC",
    },
    {
        code: "I1",
        name: "INV.ESTABLISH",
        mode: "inert",
        fidelity: "wasm-structural",
        trustZone: "Zone2-wasm",
        mechanism: "Wasm builder registry — state isolated from JS heap manipulation",
    },
    {
        code: "I2",
        name: "INV.PRESERVE",
        mode: "inert",
        fidelity: "wasm-structural",
        trustZone: "Zone2-wasm",
        mechanism: "Wasm state machine registry — transition allowlist isolated from JS",
    },
    {
        code: "C",
        name: "COMPOSE",
        mode: "inert",
        fidelity: "wasm-structural",
        trustZone: "Zone2-wasm",
        mechanism: "Wasm composition registry — chain state isolated from JS heap",
    },
];
const CONTAINED_INERT_FIDELITY_TS = [
    {
        code: "R1",
        name: "RES.CONSUME",
        mode: "contained",
        fidelity: "ts-runtime",
        trustZone: "Zone1-runtime",
        mechanism: "@csk/core allocate/consume — throws on double-spend; state in JS heap",
    },
    {
        code: "R2",
        name: "RES.RELEASE",
        mode: "contained",
        fidelity: "ts-runtime",
        trustZone: "Zone1-runtime",
        mechanism: "@csk/core managedAllocate/release — throws on double-release; state in JS heap",
    },
    {
        code: "I1",
        name: "INV.ESTABLISH",
        mode: "inert",
        fidelity: "ts-runtime",
        trustZone: "Zone1-runtime",
        mechanism: "@csk/core establish — throws if invariant not satisfied",
    },
    {
        code: "I2",
        name: "INV.PRESERVE",
        mode: "inert",
        fidelity: "ts-runtime",
        trustZone: "Zone1-runtime",
        mechanism: "@csk/core preservingTransition — throws if transition breaks invariant",
    },
    {
        code: "C",
        name: "COMPOSE",
        mode: "inert",
        fidelity: "ts-runtime",
        trustZone: "Zone1-runtime",
        mechanism: "@csk/core safeCompose — throws on type mismatch",
    },
];
/**
 * Get per-theorem fidelity reports for a given backend.
 *
 * IMMUNE theorems always report `compile-time` regardless of backend.
 * INERT/CONTAINED theorems report `wasm-structural` when WASM is in use,
 * `ts-runtime` when falling back to `@csk/core`.
 */
export function getTheoremFidelityReport(backend) {
    const containedInert = backend === "wasm"
        ? CONTAINED_INERT_FIDELITY_WASM
        : CONTAINED_INERT_FIDELITY_TS;
    return [...IMMUNE_FIDELITY_PURE_TS, ...containedInert];
}
// ── Hybrid kernel ─────────────────────────────────────────────
/**
 * Create a theorem-level accelerated kernel.
 *
 * Routes the five IMMUNE theorems to `coreKernel` (TypeScript enforcement)
 * and the five INERT/CONTAINED theorems to `wasmKernel` (Rust WASM linear
 * memory enforcement). Callers observe the same `WasmKernel` interface
 * regardless of which delegate handles which theorem.
 *
 * **Trust-zone alignment:**
 * - IMMUNE methods: Zone 1 (TypeScript type system) — violation structurally
 *   inexpressible at compile time; WASM adds no categorical benefit.
 * - INERT/CONTAINED methods: Zone 2 (Wasm linear memory) — enforcement state
 *   is outside the JS heap; JS cannot manipulate consumed/released flags.
 *
 * @param wasmKernel — A Rust WASM-backed kernel (loaded from the `.wasm` binary).
 * @param coreKernel — A `@csk/core`-backed pure-TypeScript kernel (fallback).
 * @returns A `WasmKernel` that delegates per theorem based on trust-zone
 *   optimization.
 */
export function createTheoremAcceleratedKernel(wasmKernel, coreKernel) {
    return {
        // ── IMMUNE theorems: TypeScript enforcement (Zone 1, compile-time) ──
        // These always use coreKernel. WASM provides no fidelity improvement for
        // compile-time phantom-type theorems, and the TypeScript path preserves
        // generic type parameters (e.g., Capability<R,Op>) that WASM cannot carry.
        permit_create_boundary: (...args) => coreKernel.permit_create_boundary(...args),
        permit_grant: (...args) => coreKernel.permit_grant(...args),
        permit_validate: (...args) => coreKernel.permit_validate(...args),
        permit_revoke: (...args) => coreKernel.permit_revoke(...args),
        permit_revoke_boundary: (...args) => coreKernel.permit_revoke_boundary(...args),
        separate_as_data: (...args) => coreKernel.separate_as_data(...args),
        separate_as_instruction: (...args) => coreKernel.separate_as_instruction(...args),
        separate_safe_query: (...args) => coreKernel.separate_safe_query(...args),
        origin_brand: (...args) => coreKernel.origin_brand(...args),
        origin_verify: (...args) => coreKernel.origin_verify(...args),
        origin_check_source: (...args) => coreKernel.origin_check_source(...args),
        reach_classify: (...args) => coreKernel.reach_classify(...args),
        reach_declassify: (...args) => coreKernel.reach_declassify(...args),
        reach_level: (...args) => coreKernel.reach_level(...args),
        precommit_create: (...args) => coreKernel.precommit_create(...args),
        precommit_commit: (...args) => coreKernel.precommit_commit(...args),
        precommit_cancel: (...args) => coreKernel.precommit_cancel(...args),
        precommit_state: (...args) => coreKernel.precommit_state(...args),
        precommit_description: (...args) => coreKernel.precommit_description(...args),
        // ── CONTAINED/INERT theorems: WASM enforcement (Zone 2) ──────────
        // These delegate to wasmKernel. Enforcement state lives in Wasm linear
        // memory — JS cannot reach it via heap inspection or DevTools manipulation.
        // See §8.3 in TYPESCRIPT-WASM-SEAM-ANALYSIS.md for per-theorem fidelity.
        resource_allocate: (...args) => wasmKernel.resource_allocate(...args),
        resource_consume: (...args) => wasmKernel.resource_consume(...args),
        resource_is_live: (...args) => wasmKernel.resource_is_live(...args),
        resource_live_count: (...args) => wasmKernel.resource_live_count(...args),
        managed_create: (...args) => wasmKernel.managed_create(...args),
        managed_release: (...args) => wasmKernel.managed_release(...args),
        managed_is_released: (...args) => wasmKernel.managed_is_released(...args),
        managed_use_and_release: (...args) => wasmKernel.managed_use_and_release(...args),
        inv_builder_create: (...args) => wasmKernel.inv_builder_create(...args),
        inv_builder_add_check: (...args) => wasmKernel.inv_builder_add_check(...args),
        inv_establish: (...args) => wasmKernel.inv_establish(...args),
        inv_state_name: (...args) => wasmKernel.inv_state_name(...args),
        inv_machine_create: (...args) => wasmKernel.inv_machine_create(...args),
        inv_machine_allow_transition: (...args) => wasmKernel.inv_machine_allow_transition(...args),
        inv_machine_transition: (...args) => wasmKernel.inv_machine_transition(...args),
        inv_machine_current_state: (...args) => wasmKernel.inv_machine_current_state(...args),
        inv_machine_transition_count: (...args) => wasmKernel.inv_machine_transition_count(...args),
        compose_register_operation: (...args) => wasmKernel.compose_register_operation(...args),
        compose_chain: (...args) => wasmKernel.compose_chain(...args),
        compose_validate_chain: (...args) => wasmKernel.compose_validate_chain(...args),
        // ── Seam/bridge boundary contract ────────────────────────────────
        // These exports are backend-wide metadata and admission checks. Use the
        // WASM delegate in hybrid mode so native seam descriptors and SNF checks
        // are exercised at the boundary together with theorem state.
        seam_descriptor: (...args) => wasmKernel.seam_descriptor(...args),
        seam_validate_snf_trace: (...args) => wasmKernel.seam_validate_snf_trace(...args),
        seam_define: (...args) => wasmKernel.seam_define(...args),
        bridge_witness_mint: (...args) => wasmKernel.bridge_witness_mint(...args),
        bridge_propose: (...args) => wasmKernel.bridge_propose(...args),
        bridge_admit: (...args) => wasmKernel.bridge_admit(...args),
        bridge_admit_square: (...args) => wasmKernel.bridge_admit_square(...args),
        bridge_receipt: (...args) => wasmKernel.bridge_receipt(...args),
        bridge_lifecycle: (...args) => wasmKernel.bridge_lifecycle(...args),
        bridge_witness_remaining_uses: (...args) => wasmKernel.bridge_witness_remaining_uses(...args),
        bridge_revoke_witness: (...args) => wasmKernel.bridge_revoke_witness(...args),
        bridge_admit_status: (...args) => wasmKernel.bridge_admit_status(...args),
        bridge_admit_square_status: (...args) => wasmKernel.bridge_admit_square_status(...args),
        square_decide: (...args) => wasmKernel.square_decide(...args),
        square_is_forbidden: (...args) => wasmKernel.square_is_forbidden(...args),
        // ── Metadata ────────────────────────────────────────────────────
        // csk_available_theorems combines both delegates: IMMUNE theorems come from
        // coreKernel and CONTAINED/INERT theorems come from wasmKernel. In practice,
        // both report all 10 theorems; we return the union deduplicated.
        csk_available_theorems: () => {
            const wasmSet = new Set(wasmKernel
                .csk_available_theorems()
                .split("\n")
                .map((l) => l.trim())
                .filter(Boolean));
            const coreSet = new Set(coreKernel
                .csk_available_theorems()
                .split("\n")
                .map((l) => l.trim())
                .filter(Boolean));
            return [...new Set([...wasmSet, ...coreSet])].join("\n");
        },
        csk_version: () => `${wasmKernel.csk_version()} [theorem-hybrid]`,
    };
}
//# sourceMappingURL=theorem-accelerator.js.map